import React from 'react';
import { Field } from 'react-final-form';
import { ValidationError } from '../../components';

/**
 * A simple text input that replaces the map-based LocationAutocompleteInput.
 * It keeps the same value shape ({ search, predictions, selectedPlace }) for compatibility,
 * but does not provide geocoding or autocomplete suggestions.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.inputClassName
 * @param {string?} props.placeholder
 * @param {Object} props.input - Final Form input props
 * @param {Object} props.meta - Final Form meta props
 * @param {string?} props.label
 * @param {string?} props.labelClassName
 * @param {boolean?} props.autoFocus
 * @returns {JSX.Element} location text input
 */
const LocationAutocompleteInput = props => {
  const {
    className,
    rootClassName,
    inputClassName,
    iconClassName,
    predictionsClassName,
    predictionsAttributionClassName,
    validClassName,
    labelClassName,
    label,
    placeholder,
    input,
    meta,
    hideErrorMessage,
    useDefaultPredictions,
    valueFromForm,
    autoFocus,
    inputRef,
    closeOnBlur,
    CustomIcon,
    isCurrentLocation,
    useDarkText,
    disabled,
    ...rest
  } = props;

  const value = typeof valueFromForm !== 'undefined' ? valueFromForm : input?.value;
  const searchText = typeof value === 'object' ? value?.search || '' : value || '';

  const handleChange = e => {
    const text = e.target.value;
    if (input?.onChange) {
      input.onChange({
        search: text,
        predictions: [],
        selectedPlace: text.length > 0 ? { address: text, origin: null, bounds: null } : null,
      });
    }
  };

  const handleBlur = () => {
    if (input?.onBlur) {
      const currentValue =
        typeof input.value === 'object' ? input.value : { search: input.value || '' };
      input.onBlur(currentValue);
    }
  };

  const labelEl = label ? (
    <label className={labelClassName} htmlFor={input?.name}>
      {label}
    </label>
  ) : null;

  return (
    <div className={rootClassName || className}>
      {labelEl}
      <input
        type="text"
        className={inputClassName}
        placeholder={placeholder}
        value={searchText}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={input?.onFocus}
        autoFocus={autoFocus}
        autoComplete="off"
        disabled={disabled}
        ref={inputRef}
        name={input?.name}
        id={input?.name}
      />
      {hideErrorMessage ? null : <ValidationError fieldMeta={meta} />}
    </div>
  );
};

export default LocationAutocompleteInput;

const LocationAutocompleteInputField = props => {
  const { rootClassName, labelClassName, hideErrorMessage, ...restProps } = props;
  const { input, label, meta, valueFromForm, ...otherProps } = restProps;

  const value = typeof valueFromForm !== 'undefined' ? valueFromForm : input.value;

  return (
    <LocationAutocompleteInput
      rootClassName={rootClassName}
      labelClassName={labelClassName}
      hideErrorMessage={hideErrorMessage}
      label={label}
      meta={meta}
      input={{ ...input, value }}
      {...otherProps}
    />
  );
};

export const FieldLocationAutocompleteInput = props => {
  return <Field component={LocationAutocompleteInputField} {...props} />;
};
